// 旅游笔记工具：高德地图（JS API 2.0）加载 + 附件地址 + 坐标转换
//
// 安全设计：
//  - 高德 Key 不在前端硬编码，运行时向后端 /travel/mapconfig 拉取（后端读 etc/dataservice.json，该文件不进仓库）。
//  - 安全密钥(jscode) 绝不下发前端：后端配置了 amapjscode 时，前端把高德数据请求交给后端
//    的 /_AMapService 代理，由后端补 jscode 再转发。前端网络里拿不到 jscode。
//  - 若开启了安全密钥，务必在高德控制台为 Key 绑定「安全域名白名单」——JS API Key 本身
//    必须下发到浏览器（script 标签），无法隐藏，域名白名单是最后一道防线。

import axios from "axios";
import { UseUrl } from "./config";

// 高德约定的代理固定前缀。注意：高德强制要求 _AMapService 是 origin 下的「一级路由」，
// 因此 serviceHost 必须是 {origin}/_AMapService，不能挂在 /api 之类的前缀下。
const SERVICE_HOST_SUFFIX = "/_AMapService";
const SCRIPT_ID = "amap-jsapi-script";

let configPromise = null;
let loadingPromise = null;

// 从后端获取高德前端配置：{ key, useProxy }
function fetchMapConfig() {
  if (!configPromise) {
    configPromise = axios.get(UseUrl + "/travel/mapconfig")
      .then((resp) => resp.data || {})
      .catch((err) => {
        configPromise = null;
        throw err;
      });
  }
  return configPromise;
}

// 注入高德 JS API 脚本
function injectScript(key) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.AMap));
      existing.addEventListener("error", () => reject(new Error("高德地图脚本加载失败")));
      return;
    }
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = "https://webapi.amap.com/maps?v=2.0&key=" + encodeURIComponent(key);
    script.onload = () => resolve(window.AMap);
    script.onerror = () => reject(new Error("高德地图脚本加载失败（请检查 Key、安全域名与网络）"));
    document.head.appendChild(script);
  });
}

// 加载高德地图 JS API，返回全局 AMap 对象
export function loadAMap() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("当前无浏览器环境"));
  }
  if (window.AMap) {
    return Promise.resolve(window.AMap);
  }
  if (loadingPromise) {
    return loadingPromise;
  }
  loadingPromise = fetchMapConfig().then((cfg) => {
    if (!cfg.key) {
      throw new Error("后端未配置高德地图 Key（请在 etc/dataservice.json 填写 amapkey）");
    }
    // 安全密钥只走代理：配置了才设置 serviceHost，未配置则按普通 Key 使用。
    // _AMapService 必须是一级路由，故这里不能带 UseUrl 前缀。
    if (cfg.useProxy) {
      window._AMapSecurityConfig = {
        serviceHost: window.location.origin + SERVICE_HOST_SUFFIX
      };
    }
    return injectScript(cfg.key);
  }).catch((err) => {
    // 失败后允许重试
    loadingPromise = null;
    configPromise = null;
    throw err;
  });
  return loadingPromise;
}

// 上传/附件 URL → 可直接用于 <img>/<video> 的地址
export function mediaUrl(url, base) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return (base || "") + url;
}

// ============ 坐标系转换 WGS-84 → GCJ-02 ============
// 轨迹文件（GPX/TCX）记录的是 GPS 原始坐标 WGS-84；高德地图使用国测局加密的 GCJ-02。
// 直接把 WGS-84 画到高德上会整体偏移约几百米，必须换算后再渲染。
// 这里只做「展示时换算」，库里始终保留文件原始的 WGS-84 坐标。
const GCJ_PI = 3.1415926535897932384626;
const GCJ_A = 6378245.0; // 克拉索夫斯基椭球长半轴
const GCJ_EE = 0.00669342162296594323; // 椭球偏心率平方

// 中国大陆粗略范围之外不做偏移（港澳台及境外用的是 WGS-84 明文坐标）
function outOfChina(lng, lat) {
  return !(lng > 73.66 && lng < 135.05 && lat > 3.86 && lat < 53.55);
}

function gcjTransformLat(lng, lat) {
  let ret = -100.0 + 2.0 * lng + 3.0 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * Math.sqrt(Math.abs(lng));
  ret += ((20.0 * Math.sin(6.0 * lng * GCJ_PI) + 20.0 * Math.sin(2.0 * lng * GCJ_PI)) * 2.0) / 3.0;
  ret += ((20.0 * Math.sin(lat * GCJ_PI) + 40.0 * Math.sin((lat / 3.0) * GCJ_PI)) * 2.0) / 3.0;
  ret += ((160.0 * Math.sin((lat / 12.0) * GCJ_PI) + 320 * Math.sin((lat * GCJ_PI) / 30.0)) * 2.0) / 3.0;
  return ret;
}

function gcjTransformLng(lng, lat) {
  let ret = 300.0 + lng + 2.0 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * Math.sqrt(Math.abs(lng));
  ret += ((20.0 * Math.sin(6.0 * lng * GCJ_PI) + 20.0 * Math.sin(2.0 * lng * GCJ_PI)) * 2.0) / 3.0;
  ret += ((20.0 * Math.sin(lng * GCJ_PI) + 40.0 * Math.sin((lng / 3.0) * GCJ_PI)) * 2.0) / 3.0;
  ret += ((150.0 * Math.sin((lng / 12.0) * GCJ_PI) + 300.0 * Math.sin((lng / 30.0) * GCJ_PI)) * 2.0) / 3.0;
  return ret;
}

// WGS-84 经纬度 → [GCJ-02 经度, GCJ-02 纬度]
export function wgs84ToGcj02(lng, lat) {
  if (outOfChina(lng, lat)) return [lng, lat];
  let dLat = gcjTransformLat(lng - 105.0, lat - 35.0);
  let dLng = gcjTransformLng(lng - 105.0, lat - 35.0);
  const radLat = (lat / 180.0) * GCJ_PI;
  let magic = Math.sin(radLat);
  magic = 1 - GCJ_EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  dLat = (dLat * 180.0) / (((GCJ_A * (1 - GCJ_EE)) / (magic * sqrtMagic)) * GCJ_PI);
  dLng = (dLng * 180.0) / ((GCJ_A / sqrtMagic) * Math.cos(radLat) * GCJ_PI);
  return [lng + dLng, lat + dLat];
}
